import { useMemo, useState } from 'react';
import { DatePicker } from 'antd';
import type { TableProps } from 'antd';
import { HistoryOutlined, ReloadOutlined, RightOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { useSearchParams } from 'react-router-dom';

import { DataTable, Tile } from '../../../components/shared';
import { Button, Empty, Select, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { useBangTenNguoi } from '../hooks/useNguoiDungLoc';
import { useNhatKy } from '../hooks/useNhatKy';
import { thamSoLocThaoTac } from '../nghiep-vu/danh-muc-hanh-dong';
import type { NhomHanhDong } from '../nghiep-vu/danh-muc-hanh-dong';
import { MOC_THOI_GIAN, dungKhoang } from '../nghiep-vu/khoang';
import type { MocThoiGian } from '../nghiep-vu/khoang';
import type { LocAudit, QuyetDinh, SuKienAudit } from '../types';
import { ChonNguoiDung } from './ChonNguoiDung';
import { LocThaoTac } from './LocThaoTac';
import { NganChiTiet } from './NganChiTiet';
import { NhanKetQua, useNhanNguoi, useTenHanhDong } from './NhanSuKien';

const QUYET_DINH: QuyetDinh[] = ['ALLOW', 'DENY'];

/**
 * Bảng nhật ký thao tác của **một** tenant (§8.2 `/nhat-ky/thao-tac` của đặc tả).
 *
 * Dùng chung cho ba chỗ: màn nhật ký của cổng tenant, thẻ Nhật ký trong Cài đặt
 * bảo mật, và màn Nhật ký hệ thống của cổng nền tảng (sau khi chọn tenant).
 *
 * Bộ lọc khởi tạo được từ URL (`?action=` lặp được, `?decision=`, `?user=`) để
 * màn khác dẫn thẳng tới đúng tập dòng — giống liên kết từ Tổng quan ở bản gốc.
 *
 * Lọc thao tác hai tầng: chip nhóm và ô chọn từng mã (`LocThaoTac`); lọc người
 * dùng bằng ô chọn theo tên, email (`ChonNguoiDung`).
 *
 * Khoảng thời gian tính từ một mốc `neo` chỉ đổi khi người dùng bấm "Làm mới"
 * hay đổi mốc — khoá truy vấn ổn định, không tải lại theo từng lần vẽ.
 */
export function BangNhatKy({ tenantId, nenTang }: { tenantId: string; nenTang: boolean }) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const tenHanhDong = useTenHanhDong();
  const nhanNguoi = useNhanNguoi();
  const bangTen = useBangTenNguoi(tenantId, nenTang);
  const [params] = useSearchParams();

  const [moc, setMoc] = useState<MocThoiGian>('24_gio');
  const [tuyChon, setTuyChon] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [neo, setNeo] = useState(() => Date.now());
  const [nguoi, setNguoi] = useState<string | undefined>(params.get('user') ?? undefined);
  // Tên của người vừa bấm trên bảng: dòng đã lọc chưa về thì ô lọc vẫn có tên để hiện.
  const [nguoiVuaBam, setNguoiVuaBam] = useState<{ id: string; ten: string | null } | null>(null);
  const [nhom, setNhom] = useState<NhomHanhDong[]>([]);
  const [chiTiet, setChiTiet] = useState<string[]>(() => params.getAll('action').filter(Boolean));
  const [quyetDinh, setQuyetDinh] = useState<QuyetDinh | undefined>(() => {
    const v = params.get('decision');
    return v === 'ALLOW' || v === 'DENY' ? v : undefined;
  });
  const [dangXem, setDangXem] = useState<SuKienAudit | null>(null);

  const khoang = useMemo(
    () =>
      dungKhoang(moc, neo, {
        tu: tuyChon?.[0]?.valueOf() ?? null,
        den: tuyChon?.[1]?.valueOf() ?? null,
      }),
    [moc, neo, tuyChon],
  );

  const loc = useMemo<LocAudit | null>(
    () =>
      khoang.ok
        ? {
            tenant_id: tenantId,
            ...khoang.khoang,
            ...(nguoi ? { user_id: nguoi } : {}),
            ...thamSoLocThaoTac(nhom, chiTiet),
            ...(quyetDinh ? { decision: quyetDinh } : {}),
          }
        : null,
    [khoang, tenantId, nguoi, nhom, chiTiet, quyetDinh],
  );

  const q = useNhatKy(loc, nenTang);
  const dong = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  /** Server tra từ danh bạ tenant; nhân sự nền tảng không có trong đó nên lấy ở bảng tên phía web. */
  const tenCua = (row: SuKienAudit) => row.user_name ?? (row.user_id ? bangTen.get(row.user_id) : null) ?? null;
  const dongCuaNguoi = nguoi ? dong.find((r) => r.user_id === nguoi) : undefined;
  const tenNguoiDangLoc = (dongCuaNguoi && tenCua(dongCuaNguoi)) ?? (nguoiVuaBam?.id === nguoi ? nguoiVuaBam?.ten : null);
  const coDieuKien = Boolean(nguoi || nhom.length > 0 || chiTiet.length > 0 || quyetDinh);

  const xoaBoLoc = () => {
    setNguoi(undefined);
    setNhom([]);
    setChiTiet([]);
    setQuyetDinh(undefined);
  };

  const loiKhoang = !khoang.ok
    ? khoang.loi === 'qua_dai'
      ? t('nhatKy.loc.quaDai', { so: soVN(khoang.toiDa ?? 90, locale) })
      : khoang.loi === 'nguoc'
        ? t('nhatKy.loc.nguoc')
        : t('nhatKy.loc.chonKhoang')
    : undefined;

  const cot: TableProps<SuKienAudit>['columns'] = [
    {
      title: t('nhatKy.cot.thoiDiem'),
      dataIndex: 'created_at',
      width: 150,
      render: (v: string) => (
        <time dateTime={v} className="whitespace-nowrap font-mono text-xs text-content-secondary">
          {ngayGioVN(v, locale)}
        </time>
      ),
    },
    {
      title: t('nhatKy.cot.nguoi'),
      width: 200,
      render: (_v, row) => (
        <div className="flex min-w-0 flex-col">
          {row.user_id ? (
            <button
              type="button"
              className="truncate rounded text-left text-xs text-content-link underline decoration-dotted underline-offset-2 hover:text-primary-700"
              title={tenCua(row) ? t('nhatKy.chiNguoiNay') : row.user_id}
              onClick={(e) => {
                e.stopPropagation();
                setNguoiVuaBam({ id: row.user_id!, ten: tenCua(row) });
                setNguoi(row.user_id!);
              }}
            >
              {nhanNguoi(tenCua(row), row.user_id).nhan}
            </button>
          ) : (
            <span className="text-sm text-content-secondary">{t('nhatKy.heThong')}</span>
          )}
          <span className="truncate font-mono text-2xs text-content-muted">{row.ip ?? KHONG_CO}</span>
        </div>
      ),
    },
    {
      title: t('nhatKy.cot.thaoTac'),
      width: 320,
      render: (_v, row) => (
        <div className="flex min-w-0 flex-col">
          <span className="text-sm text-content-primary">{tenHanhDong(row.action)}</span>
          {row.resource && (
            <span className="truncate font-mono text-2xs text-content-muted" title={row.resource}>
              {row.resource}
            </span>
          )}
        </div>
      ),
    },
    {
      title: t('nhatKy.cot.ketQua'),
      dataIndex: 'decision',
      width: 120,
      render: (v: QuyetDinh) => <NhanKetQua quyetDinh={v} />,
    },
    {
      title: <span className="sr-only">{t('nhatKy.moChiTiet')}</span>,
      width: 56,
      align: 'center',
      // Một hành động duy nhất: nút icon trần (ADR-UI-0005), đường vào ngăn bằng bàn phím.
      render: (_v, row) => (
        <Button
          variant="ghost"
          size="sm"
          icon={<RightOutlined />}
          title={t('nhatKy.moChiTiet')}
          aria-label={t('nhatKy.moChiTiet')}
          onClick={(e) => {
            e.stopPropagation();
            setDangXem(row);
          }}
        />
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-default bg-surface p-3 shadow-sm">
        <div className="flex flex-wrap items-start gap-2">
          <Select<MocThoiGian>
            className="w-40"
            aria-label={t('nhatKy.loc.khoang')}
            value={moc}
            options={MOC_THOI_GIAN.map((m) => ({ value: m, label: t(`nhatKy.moc.${m}` as KhoaDich) }))}
            onChange={(v) => {
              setMoc(v);
              setNeo(Date.now());
            }}
          />
          {moc === 'tuy_chon' && (
            <DatePicker.RangePicker
              showTime={{ format: 'HH:mm' }}
              format="DD/MM/YYYY HH:mm"
              aria-label={t('nhatKy.loc.khoang')}
              status={loiKhoang ? 'error' : undefined}
              value={tuyChon}
              onChange={(v) => setTuyChon(v as [Dayjs | null, Dayjs | null] | null)}
            />
          )}
          <ChonNguoiDung tenantId={tenantId} nenTang={nenTang} value={nguoi} tenDaBiet={tenNguoiDangLoc} onChange={setNguoi} />
          <Select<QuyetDinh>
            className="w-36"
            allowClear
            aria-label={t('nhatKy.loc.ketQua')}
            placeholder={t('nhatKy.loc.ketQua')}
            value={quyetDinh}
            options={QUYET_DINH.map((v) => ({ value: v, label: t(`nhatKy.ketQua.${v}` as KhoaDich) }))}
            onChange={(v) => setQuyetDinh(v)}
          />
          <Button
            variant="outline"
            size="md"
            icon={<ReloadOutlined />}
            loading={q.isFetching && !q.isFetchingNextPage}
            onClick={() => setNeo(Date.now())}
          >
            {t('chung.lamMoi')}
          </Button>
          {coDieuKien && (
            <Button variant="ghost" size="md" onClick={xoaBoLoc}>
              {t('chung.xoaBoLoc')}
            </Button>
          )}
        </div>
        <div className="mt-3 border-t border-default pt-3">
          <LocThaoTac nhom={nhom} chiTiet={chiTiet} onDoiNhom={setNhom} onDoiChiTiet={setChiTiet} />
        </div>
        {loiKhoang && (
          <p role="alert" className="mt-2 text-xs text-danger-600">
            {loiKhoang}
          </p>
        )}
      </div>

      <Tile className="overflow-hidden">
        {q.error ? (
          <div role="alert" className="space-y-3 p-6">
            <p className="text-sm text-danger-700">{t('nhatKy.loi', { loi: dichLoi(q.error) })}</p>
            <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
              {t('chung.thuLai')}
            </Button>
          </div>
        ) : q.isLoading && loc !== null ? (
          <Skeleton className="m-4 h-72" />
        ) : (
          <>
            <DataTable<SuKienAudit>
              caption={t('nhatKy.caption')}
              dataSource={dong}
              columns={cot}
              size="small"
              pagination={false}
              onRow={(row) => ({ onClick: () => setDangXem(row), className: 'cursor-pointer' })}
              locale={{
                emptyText: coDieuKien ? (
                  // Rỗng vì bộ lọc: không mô tả, không nút — "Xoá bộ lọc" đã ở thanh lọc.
                  <Empty icon={HistoryOutlined} title={t('nhatKy.rongLoc')} />
                ) : (
                  <Empty
                    icon={HistoryOutlined}
                    title={t('nhatKy.rongThat')}
                    description={t('nhatKy.rongThatMo')}
                    action={
                      moc !== '30_ngay' && moc !== 'tuy_chon' ? (
                        <Button
                          variant="outline"
                          size="md"
                          onClick={() => {
                            setMoc('30_ngay');
                            setNeo(Date.now());
                          }}
                        >
                          {t('nhatKy.xem30Ngay')}
                        </Button>
                      ) : undefined
                    }
                  />
                ),
              }}
            />
            {dong.length > 0 && (
              <div className="flex items-center justify-center gap-3 border-t border-default px-4 py-5">
                {q.isFetchingNextPage ? (
                  <Skeleton className="h-8 w-40" />
                ) : q.hasNextPage ? (
                  <Button variant="outline" size="sm" onClick={() => void q.fetchNextPage()}>
                    {t('chung.taiThem')}
                  </Button>
                ) : (
                  <p className="text-xs text-content-muted" role="status">
                    {t('nhatKy.hetBanGhi', { so: soVN(dong.length, locale) })}
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </Tile>

      <NganChiTiet suKien={dangXem} tenNguoi={dangXem && tenCua(dangXem)} onClose={() => setDangXem(null)} />
    </div>
  );
}
